import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { usersApi, User, CreateUserRequest, UpdateUserRequest, GetUsersParams } from '../services/usersApi'

interface UsersState {
  users: User[]
  loading: boolean
  error: string | null
  currentTenantId: string | null
  pagination: {
    page: number
    limit: number
    total: number
  }
}

interface UsersActions {
  fetchUsersByTenant: (tenantId: string) => Promise<void>
  fetchUsers: (params?: GetUsersParams) => Promise<void>
  createUser: (userData: CreateUserRequest) => Promise<void>
  updateUser: (userId: string, userData: UpdateUserRequest) => Promise<void>
  deleteUser: (userId: string) => Promise<void>
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
}

type UsersStore = UsersState & UsersActions

export const useUsersStore = create<UsersStore>()(
  persist(
    (set, get) => ({
      // Initial state
      users: [],
      loading: false,
      error: null,
      currentTenantId: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0
      },

      // Actions
      fetchUsersByTenant: async (tenantId: string) => {
        set({ loading: true, error: null, currentTenantId: tenantId })
        
        try {
          const response = await usersApi.getUsersByTenant(tenantId)
          console.log('Users by tenant response:', response)
          
          if (response.success && response.data) {
            // Handle nested response structure: response.data.data
            const usersData = (response.data as any).data || []
            set({
              users: usersData,
              loading: false,
              error: null,
            })
          } else {
            set({
              users: [],
              loading: false,
              error: 'Failed to fetch users',
            })
          }
        } catch (error: any) {
          set({
            users: [],
            loading: false,
            error: error.message || 'Failed to fetch users',
          })
        }
      },

      fetchUsers: async (params: GetUsersParams = {}) => {
        console.log('Fetching users with params:', params)
        set({ loading: true, error: null })
        
        try {
          const response = await usersApi.getUsers(params)
          console.log('All users response:', response)
          
          if (response.success && response.data) {
            console.log('Setting users data:', response.data)
            set({
              users: response.data,
              loading: false,
              error: null,
              pagination: {
                page: params.page || 1,
                limit: params.limit || 10,
                total: response.data.length // This will be updated when we get the actual total from API
              }
            })
          } else {
            console.log('No users data or failed response:', response)
            set({
              users: [],
              loading: false,
              error: 'Failed to fetch users',
            })
          }
        } catch (error: any) {
          console.error('Error fetching users:', error)
          set({
            users: [],
            loading: false,
            error: error.message || 'Failed to fetch users',
          })
        }
      },

      createUser: async (userData: CreateUserRequest) => {
        set({ loading: true, error: null })
        
        try {
          const response = await usersApi.createUser(userData)
          
          if (response.success && response.data) {
            // Don't add the user to the list here, let the component refresh the list
            // This is because the API returns roles as IDs, but the list expects role objects
            set({
              loading: false,
              error: null,
            })
          } else {
            set({
              loading: false,
              error: 'Failed to create user',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to create user',
          })
        }
      },

      updateUser: async (userId: string, userData: UpdateUserRequest) => {
        set({ loading: true, error: null })
        
        try {
          const response = await usersApi.updateUser(userId, userData)
          console.log('Update user response:', response)
          
          if (response.success && response.data) {
            const { users } = get()
            set({
              users: users.map(user => 
                user._id === userId ? response.data! : user
              ),
              loading: false,
              error: null,
            })
          } else {
            console.error('Update user failed:', response)
            set({
              loading: false,
              error: 'Failed to update user',
            })
          }
        } catch (error: any) {
          console.error('Update user error:', error)
          set({
            loading: false,
            error: error.message || 'Failed to update user',
          })
        }
      },

      deleteUser: async (userId: string) => {
        set({ loading: true, error: null })
        
        try {
          const response = await usersApi.deleteUser(userId)
          
          if (response.success) {
            const { users } = get()
            set({
              users: users.filter(user => user._id !== userId),
              loading: false,
              error: null,
            })
          } else {
            set({
              loading: false,
              error: 'Failed to delete user',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to delete user',
          })
        }
      },

      setLoading: (loading: boolean) => {
        set({ loading })
      },

      setError: (error: string | null) => {
        set({ error })
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'users-storage',
      partialize: (state) => ({
        users: state.users,
        currentTenantId: state.currentTenantId,
      }),
    }
  )
)
